import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  CircularProgress
} from '@mui/material';
import { PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { useAppUser } from '@/contexts/AppUserProvider';

interface SupportSubmissionFormProps {
  onSuccess?: () => void;
}

type SubmissionType = 'bug' | 'feature' | 'question' | 'other';

export const SupportSubmissionForm: React.FC<SupportSubmissionFormProps> = ({
  onSuccess
}) => {
  const theme = useTheme();
  const { appUser } = useAppUser();
  const [submissionType, setSubmissionType] = useState<SubmissionType>('question');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      setErrorMessage('Please fill in all required fields.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // TODO: Replace with actual API endpoint when backend is ready
      // For now, simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real implementation, you would call an API like:
      // await fetch('/api/support/submit', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     type: submissionType,
      //     subject,
      //     message,
      //     userId: appUser?.id,
      //     userEmail: appUser?.email
      //   })
      // });

      setSubmitStatus('success');
      setSubject('');
      setMessage('');
      setSubmissionType('question');
      
      if (onSuccess) {
        onSuccess();
      }

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit your request. Please try again or contact your system administrator.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        mt: 3,
        p: 3,
        backgroundColor: theme.palette.background.default,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Submit a Support Request
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Have a question, found a bug, or have a feature request? We'd love to hear from you!
      </Typography>

      {submitStatus === 'success' && (
        <Alert
          severity="success"
          icon={<CheckCircle size={20} />}
          sx={{ mb: 3 }}
          onClose={() => setSubmitStatus('idle')}
        >
          Your support request has been submitted successfully! We'll get back to you soon.
        </Alert>
      )}

      {submitStatus === 'error' && errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitStatus('idle')}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormControl fullWidth>
          <InputLabel id="submission-type-label">Request Type</InputLabel>
          <Select
            labelId="submission-type-label"
            id="submission-type"
            value={submissionType}
            label="Request Type"
            onChange={(e) => setSubmissionType(e.target.value as SubmissionType)}
            sx={{
              backgroundColor: theme.palette.background.paper
            }}
          >
            <MenuItem value="question">💬 Question</MenuItem>
            <MenuItem value="bug">🐛 Bug Report</MenuItem>
            <MenuItem value="feature">✨ Feature Request</MenuItem>
            <MenuItem value="other">📝 Other</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          required
          label="Subject"
          placeholder="Brief description of your request"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{
            backgroundColor: theme.palette.background.paper
          }}
        />

        <TextField
          fullWidth
          required
          multiline
          rows={6}
          label="Message"
          placeholder="Please provide as much detail as possible..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{
            backgroundColor: theme.palette.background.paper
          }}
        />

        {appUser?.email && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Your request will be sent from: {appUser.email}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || submitStatus === 'success'}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PaperPlaneTilt size={18} />
            )
          }
          sx={{
            mt: 1,
            py: 1.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#1A1A1A',
            color: '#fff',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#2A2A2A'
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            }
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Request'}
        </Button>
      </Box>
    </Box>
  );
};

